/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Event Dashboard Hooks - React Query for Domain Events
 * ============================================================================
 *
 * Custom hooks for domain event dashboard data fetching and mutations.
 * Provides event monitoring, listener control, and metrics tracking.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  eventsService,
  EventDashboardSummary,
  EventListenerInfo,
  EventMetricsDto,
  ToggleResult,
} from '@/services/superadmin/events.service';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const eventDashboardKeys = {
  all: ['events'] as const,
  summary: () => [...eventDashboardKeys.all, 'summary'] as const,
  listeners: () => [...eventDashboardKeys.all, 'listeners'] as const,
  listenersByModule: (moduleCode: string) => [...eventDashboardKeys.listeners(), moduleCode] as const,
  modules: () => [...eventDashboardKeys.all, 'modules'] as const,
  metrics: () => [...eventDashboardKeys.all, 'metrics'] as const,
  eventMetrics: (eventType: string) => [...eventDashboardKeys.metrics(), eventType] as const,
  eventTrend: (eventType: string, days: number) =>
    [...eventDashboardKeys.all, 'trend', eventType, days] as const,
};

/**
 * Hook to fetch the complete event dashboard summary
 */
export function useEventDashboardSummary() {
  return useQuery<EventDashboardSummary, ApiError>({
    queryKey: eventDashboardKeys.summary(),
    queryFn: () => eventsService.getDashboardSummary(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
}

/**
 * Hook to fetch all event listeners
 */
export function useEventListeners() {
  return useQuery<EventListenerInfo[], ApiError>({
    queryKey: eventDashboardKeys.listeners(),
    queryFn: () => eventsService.getAllListeners(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch listeners by module
 */
export function useEventListenersByModule(moduleCode: string) {
  return useQuery<EventListenerInfo[], ApiError>({
    queryKey: eventDashboardKeys.listenersByModule(moduleCode),
    queryFn: () => eventsService.getListenersByModule(moduleCode),
    enabled: !!moduleCode,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch all module codes
 */
export function useEventModules() {
  return useQuery<string[], ApiError>({
    queryKey: eventDashboardKeys.modules(),
    queryFn: () => eventsService.getModuleCodes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all event metrics for today
 */
export function useEventMetrics() {
  return useQuery<EventMetricsDto[], ApiError>({
    queryKey: eventDashboardKeys.metrics(),
    queryFn: () => eventsService.getAllMetrics(),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/**
 * Hook to fetch metrics for a specific event type
 */
export function useEventTypeMetrics(eventType: string) {
  return useQuery<EventMetricsDto, ApiError>({
    queryKey: eventDashboardKeys.eventMetrics(eventType),
    queryFn: () => eventsService.getEventMetrics(eventType),
    enabled: !!eventType,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch trend data for an event type
 */
export function useEventTrend(eventType: string, days: number = 7) {
  return useQuery<EventMetricsDto[], ApiError>({
    queryKey: eventDashboardKeys.eventTrend(eventType, days),
    queryFn: () => eventsService.getEventTrend(eventType, days),
    enabled: !!eventType,
    staleTime: 60 * 1000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Hook for enabling a listener
 */
export function useEnableListener() {
  const queryClient = useQueryClient();

  return useMutation<
    ToggleResult,
    ApiError,
    { eventType: string; listenerClass?: string; persist?: boolean }
  >({
    mutationFn: ({ eventType, listenerClass, persist = true }) =>
      eventsService.enableListener(eventType, listenerClass, persist),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.summary() });
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.listeners() });
    },
  });
}

/**
 * Hook for disabling a listener
 */
export function useDisableListener() {
  const queryClient = useQueryClient();

  return useMutation<
    ToggleResult,
    ApiError,
    { eventType: string; listenerClass?: string; persist?: boolean }
  >({
    mutationFn: ({ eventType, listenerClass, persist = true }) =>
      eventsService.disableListener(eventType, listenerClass, persist),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.summary() });
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.listeners() });
    },
  });
}

/**
 * Hook for toggling a listener
 */
export function useToggleListener() {
  const queryClient = useQueryClient();

  return useMutation<
    ToggleResult,
    ApiError,
    { eventType: string; listenerClass?: string; persist?: boolean }
  >({
    mutationFn: ({ eventType, listenerClass, persist = true }) =>
      eventsService.toggleListener(eventType, listenerClass, persist),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.summary() });
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.listeners() });
    },
  });
}

/**
 * Hook for clearing the listener cache
 */
export function useClearEventCache() {
  const queryClient = useQueryClient();

  return useMutation<string, ApiError, void>({
    mutationFn: () => eventsService.clearCache(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventDashboardKeys.all });
    },
  });
}
